import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { VersionNavComponent } from '../../shared/components/version-nav/version-nav.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AuthService } from '../../services/auth.service';

type UserType = 'brand' | 'agency';

interface OptionalFeature {
  key: string;
  label: string;
}

interface PricingBreakdown {
  base: number;
  addons: number;
  total: number;
  savings: number;
  commissionRate: number;
  commissionValue: number;
}

@Component({
  selector: 'app-pricing',
  standalone: true,
  imports: [CommonModule, MatAngularModule, MatSlideToggleModule, VersionNavComponent, HeaderComponent],
  templateUrl: './pricing.component.html',
  styleUrl: './pricing.component.scss'
})
export class PricingComponent {
  isAuthenticated: boolean = false;
  isAnnual: boolean = false;

  userTypes: { value: UserType; label: string }[] = [
    { value: 'brand', label: 'Brand' },
    { value: 'agency', label: 'Agency' }
  ];

  selectedUserType: UserType = 'brand';
  projectsCount: number = 3;
  promptUsage: number = 500;
  extraUsers: number = 0;
  selectedFeatureKeys: string[] = [];

  staticPlanHighlights: string[] = [
    'AI brand insights, category analysis, and sentiment tracking',
    'AI content index and channel-ready content ideas',
    'AI-ready site audit and project performance visibility'
  ];

  optionalFeatures: OptionalFeature[] = [
    { key: 'media-influence', label: 'AI Search Media Influence' },
    { key: 'activation-consultancy', label: 'Activation and Consultancy' },
    { key: 'project-management', label: 'Project Management Tool' }
  ];

  faqItems: { q: string; a: string }[] = [
    {
      q: 'How does annual billing work?',
      a: 'Annual billing gives one month free equivalent versus monthly total for the same usage profile.'
    },
    {
      q: 'Is consultancy included in calculator pricing?',
      a: 'No. Consultancy is shown as an add-on interest only and quoted separately by our team.'
    },
    {
      q: 'Can I request a custom enterprise package?',
      a: 'Yes. Use contact options below for custom scope, data integrations, and rollout support.'
    },
    {
      q: 'What happens if we outgrow the standard plan?',
      a: 'You can move into custom pricing without losing continuity. We adjust limits, governance, and reporting for your next growth stage.'
    },
    {
      q: 'How do you define a tracked prompt?',
      a: 'A tracked prompt is a recurring AI-search query monitored for visibility, citations, competitive presence, and content opportunity signals.'
    }
  ];

  testimonials: { quote: string; name: string; role: string; avatarUrl: string }[] = [
    {
      quote: 'The pricing calculator helped us scope exactly what we needed before we booked a demo.',
      name: 'Amelia R.',
      role: 'Head of Growth, D2C Brand',
      avatarUrl: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=160&q=80'
    },
    {
      quote: 'Clear pricing direction and custom options made agency onboarding much easier.',
      name: 'James P.',
      role: 'Strategy Director, Agency Partner',
      avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=160&q=80'
    }
  ];

  constructor(
    private authService: AuthService
  ) {
    this.isAuthenticated = this.authService.isAuthenticated() && !this.authService.isTokenExpired();
  }

  get staticMonthlyPrice(): number {
    return this.selectedUserType === 'agency' ? 1000 : 200;
  }

  get capabilityRows(): { feature: string; standard: string; custom: string }[] {
    return [
      { feature: 'Prompt tracking volume', standard: this.selectedUserType === 'agency' ? '250 / month' : '150 / month', custom: 'Scaled by goal and market coverage' },
      { feature: 'Site audits', standard: this.selectedUserType === 'agency' ? 'Per brand monthly' : '1 monthly audit', custom: 'Advanced multi-domain audits' },
      { feature: 'AI visibility insights', standard: 'Included', custom: 'Deeper strategic and competitive layers' },
      { feature: 'Reporting', standard: 'Standard reporting', custom: 'Executive-ready and custom dashboards' },
      { feature: 'Integrations', standard: 'Basic exports', custom: 'API and workflow integrations' },
      { feature: 'Support model', standard: 'Email support', custom: 'Priority team + strategic check-ins' }
    ];
  }

  get staticAnnualPrice(): number {
    return this.staticMonthlyPrice * 11;
  }

  get staticBasePrice(): number {
    return this.isAnnual ? this.staticAnnualPrice : this.staticMonthlyPrice;
  }

  onToggleChange(event: any): void {
    this.isAnnual = !!event.checked;
  }

  setUserType(type: UserType): void {
    this.selectedUserType = type;
    if (type === 'agency') {
      this.projectsCount = Math.max(this.projectsCount, 2);
    }
  }

  onProjectsInput(value: string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    this.projectsCount = Math.min(50, Math.max(1, Math.round(parsed)));
  }

  onPromptInput(value: string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    this.promptUsage = Math.min(5000, Math.max(100, Math.round(parsed)));
  }

  onExtraUsersInput(value: string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    this.extraUsers = Math.min(1000, Math.max(0, Math.round(parsed)));
  }

  toggleFeature(key: string): void {
    if (this.selectedFeatureKeys.includes(key)) {
      this.selectedFeatureKeys = this.selectedFeatureKeys.filter((featureKey) => featureKey !== key);
      return;
    }
    this.selectedFeatureKeys = [...this.selectedFeatureKeys, key];
  }

  isFeatureSelected(key: string): boolean {
    return this.selectedFeatureKeys.includes(key);
  }

  getBillingPeriod(): string {
    return this.isAnnual ? 'year' : 'month';
  }

  get calculatorBreakdown(): PricingBreakdown {
    const base = this.staticBasePrice;
    const includedProjects = this.selectedUserType === 'agency' ? 3 : 1;
    const includedPrompts = this.selectedUserType === 'agency' ? 250 : 150;
    const extraProjects = Math.max(0, this.projectsCount - includedProjects);
    const extraPrompts = Math.max(0, this.promptUsage - includedPrompts);
    const projectUnitCost = this.selectedUserType === 'agency' ? 100 : 50;
    const promptUnitCost = 25;
    const promptBlocks = Math.ceil(extraPrompts / 100);

    const userCost = this.extraUsers * 3;
    const addons = (extraProjects * projectUnitCost) + (promptBlocks * promptUnitCost) + userCost;
    const total = base + addons;

    const customBuildEstimate = Math.round(total * 1.2);
    const savings = Math.max(0, customBuildEstimate - total);
    const commissionRate = this.selectedUserType === 'agency'
      ? (this.projectsCount > 10 ? 0.17 : 0.15)
      : 0;
    const commissionValue = total * commissionRate;

    return {
      base,
      addons,
      total,
      savings,
      commissionRate,
      commissionValue
    };
  }

  selectPlan(): void {
    console.log('Navigate to quote or checkout');
  }

  formatPrice(price: number): string {
    return `£${Math.round(price).toLocaleString()}`;
  }
}
